// ─────────────────────────────────────────────────────────────────────────────
// Role-specific dashboard spotlights, data layer.
//
// Each non-owner role gets its own hero card (legacy: separate #*-spotlight-card
// articles, one per data-effective-role). This module holds the typed fetches +
// pure computations for those cards, ported 1:1 from the legacy wiring files so
// the numbers match. Currently: Band Member (band-member-wiring.js). The others
// (Production / TM / Booking / Merch) get added here as we build them.
// ─────────────────────────────────────────────────────────────────────────────

import { apiGet } from './api';
import { useCachedData } from './dataCache';
import { flyerSrc , isHiddenForMe, visibleShows } from './booking';
/* show-scope 2026-07-29 — explicit viewer for the scope filter. */
import { fetchCurrentUser } from './dashboard';
import type { RawBookingEvent, LineupAct } from './booking';
import type { ScheduleResponse } from './types';

// Unwrap the /api/data/schedule envelope (bare array | {value} | {data:{value}}).
// Mirrors booking.ts unwrapArr (not exported), kept local to avoid coupling.
function unwrapEvents(res: ScheduleResponse | null): RawBookingEvent[] {
  if (!res) return [];
  const raw = (res as { data?: unknown }).data ?? res;
  const val = Array.isArray(raw)
    ? raw
    : Array.isArray((raw as { value?: unknown }).value)
      ? (raw as { value: unknown[] }).value
      : [];
  /* hide-for-me 2026-07-17: rows the current user soft-deleted carry
     responses[uid]='pass' (lib/booking.ts isHiddenForMe), never spotlight one. */
  return (val as RawBookingEvent[]).filter((e) => e && e.date && !isHiddenForMe(e));
}

// ── Shared date / confirmed helpers (band-member-wiring.js parity) ─────────────
function parseEventDate(ev: RawBookingEvent): Date | null {
  if (!ev || !ev.date) return null;
  const d = new Date(ev.date + 'T12:00:00');
  return isNaN(d.getTime()) ? null : d;
}

export function isShowConfirmed(ev: RawBookingEvent): boolean {
  if (!ev) return false;
  if ((ev.type || 'show') !== 'show') return false;
  if (ev.confirmed === true) return true;
  const s = String(ev.status || '').toLowerCase();
  return s === 'confirmed' || s === 'booked' || s === 'locked';
}

function findNextShow(events: ReadonlyArray<RawBookingEvent>): RawBookingEvent | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcoming = events.filter((ev) => {
    if (!isShowConfirmed(ev)) return false;
    const d = parseEventDate(ev);
    return d != null && d >= today;
  });
  upcoming.sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
  return upcoming[0] || null;
}

function findRecentPastShows(events: ReadonlyArray<RawBookingEvent>, limit = 5): RawBookingEvent[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const past = events.filter((ev) => {
    if (!isShowConfirmed(ev)) return false;
    const d = parseEventDate(ev);
    return d != null && d < today;
  });
  past.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
  return past.slice(0, limit);
}

// YTD income with this band: sum event.financials.perPerson across confirmed
// shows dated Jan 1 → today (future shows don't count, only money earned).
// Fallback: totalPay / memberCount when perPerson is absent (older blobs).
function computeYtdIncome(events: ReadonlyArray<RawBookingEvent>): number {
  const year = new Date().getFullYear();
  const jan1 = new Date(year, 0, 1);
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  let total = 0;
  for (const ev of events) {
    if (!isShowConfirmed(ev)) continue;
    const d = parseEventDate(ev);
    if (!d || d < jan1 || d > today) continue;
    const fin = ev.financials;
    if (!fin) continue;
    const pp = Number(fin.perPerson);
    if (!isNaN(pp) && pp > 0) {
      total += pp;
      continue;
    }
    const tot = Number(fin.totalPay);
    const mc = Number(fin.memberCount);
    if (!isNaN(tot) && !isNaN(mc) && mc > 0) total += tot / mc;
  }
  return total;
}

// ── Band Member spotlight ─────────────────────────────────────────────────────
export interface BandMemberShowTile {
  readonly id: string;
  readonly date: string;
  readonly venue: string;
  readonly city: string;
  /** Resolved flyer image URL, or '' when none. */
  readonly flyerUrl: string;
  /** The post-show media link (photos/videos), or '' when none. */
  readonly mediaLink: string;
}

export interface BandMemberSpotlightData {
  readonly nextShow: RawBookingEvent | null;
  readonly pastShows: ReadonlyArray<BandMemberShowTile>;
  readonly ytd: number;
}

function toTile(ev: RawBookingEvent): BandMemberShowTile {
  return {
    id: String(ev.id ?? ev.date ?? ''),
    date: String(ev.date || ''),
    venue: ev.name || ev.venue || 'Untitled',
    city: ev.city || '',
    flyerUrl: flyerSrc(ev),
    mediaLink: String(ev.mediaLink || '').trim(),
  };
}

/** Pure compute (exported for testing). */
export function computeBandMemberSpotlight(
  events: ReadonlyArray<RawBookingEvent>,
): BandMemberSpotlightData {
  return {
    nextShow: findNextShow(events),
    pastShows: findRecentPastShows(events, 5).map(toTile),
    ytd: computeYtdIncome(events),
  };
}

const EMPTY_BM: BandMemberSpotlightData = { nextShow: null, pastShows: [], ytd: 0 };

/** Band-scoped (dataCache keys by active band id) hook for the BM spotlight. */
export function useBandMemberSpotlight(): { data: BandMemberSpotlightData; loading: boolean } {
  const { data, loading } = useCachedData<BandMemberSpotlightData>('bm-spotlight', async () => {
    /* show-scope 2026-07-29 — member-tier seats see only their own dates.
       User fetched alongside, NOT via getCachedUser: on a cold load the
       cache races this fetch and a null viewer would blank the card. */
    const [res, user] = await Promise.all([
      apiGet<ScheduleResponse>('auth', '/api/data/schedule').catch(() => null),
      fetchCurrentUser().catch(() => null),
    ]);
    return computeBandMemberSpotlight(visibleShows(unwrapEvents(res), user));
  });
  return { data: data ?? EMPTY_BM, loading };
}

// ── Tour Manager spotlight ────────────────────────────────────────────────────
// 1:1 port of tour-manager-wiring.js: a today/tomorrow operational hero (tonight's
// hotel, next venue, lobby call) + a Next-3 shows ticket list. Top merch reuses
// the dashboard's existing merch bestsellers (passed in by the component).

// Exact day delta (negative = past, Infinity = no date), TM needs 0 = today.
function tmDaysUntil(iso: string | undefined): number {
  if (!iso) return Infinity;
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return Infinity;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

function isoToday(): string {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t.getFullYear() + '-' + String(t.getMonth() + 1).padStart(2, '0') + '-' + String(t.getDate()).padStart(2, '0');
}

// Pull the first defined load-in time off a show (string or per-band map).
function loadInTime(ev: RawBookingEvent | null): string {
  const dos = (ev && ev.dayOfShow) || {};
  if (dos.loadInProd) return String(dos.loadInProd);
  const li = dos.loadIn;
  if (li && typeof li === 'object') return Object.values(li).filter(Boolean)[0] || '';
  return (typeof li === 'string' && li) || '';
}

export interface TmShowRow {
  readonly id: string;
  readonly dayNum: string;
  readonly monthAbbr: string;
  readonly venue: string;
  readonly soldLabel: string;
  readonly capLabel: string;
}

export interface TourManagerSpotlightData {
  readonly hasShow: boolean;
  readonly eyebrow: string;
  readonly title: string;
  readonly sub: string;
  readonly guarantee: string;
  readonly hotel: string;
  readonly next: string;
  readonly next3: ReadonlyArray<TmShowRow>;
}

function toTmRow(ev: RawBookingEvent): TmShowRow {
  const d = ev.date ? new Date(ev.date + 'T00:00:00') : null;
  const valid = d != null && !isNaN(d.getTime());
  const venue = ev.name || ev.venue || 'Untitled';
  const city = ev.city || '';
  const door = (ev.settlement && ev.settlement.door) || {};
  const sold = (Number(door.ga_sold) || 0) + (Number(door.vip_sold) || 0);
  const cap = Number(ev.capacity) || 0;
  return {
    id: String(ev.id ?? ''),
    dayNum: valid ? String(d!.getDate()) : '-',
    monthAbbr: valid ? d!.toLocaleDateString('en-US', { month: 'short' }).toUpperCase() : '',
    venue: venue + (city ? ' · ' + city : ''),
    soldLabel: cap ? sold + ' / ' + cap : sold ? String(sold) : '-',
    capLabel: cap ? 'capacity ' + cap : 'sold',
  };
}

export function computeTourManagerSpotlight(
  events: ReadonlyArray<RawBookingEvent>,
): TourManagerSpotlightData {
  const todayIso = isoToday();
  const confirmedUpcoming = events
    .filter((ev) => isShowConfirmed(ev) && String(ev.date || '') >= todayIso)
    .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
  const hero = confirmedUpcoming[0] || null;
  const nextAfter = hero
    ? events
        .filter((ev) => isShowConfirmed(ev) && String(ev.date || '') > String(hero.date || ''))
        .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')))[0] || null
    : null;

  // Hero copy.
  let eyebrow = '★ Today';
  let title = 'No show today';
  let sub =
    "When the band is on the road this card surfaces tonight's hotel, tomorrow's drive, and the lobby call so you're not hunting through the schedule mid-tour.";
  if (hero) {
    const dd = tmDaysUntil(hero.date);
    eyebrow = dd === 0 ? '★ Today' : dd === 1 ? '★ Tomorrow' : '★ In ' + dd + ' days';
    const venue = hero.name || hero.venue || 'Untitled venue';
    const city = hero.city || '';
    title = city ? venue + ', ' + city : venue;
    const bits: string[] = [];
    const dt = hero.date ? new Date(hero.date + 'T00:00:00') : null;
    if (dt && !isNaN(dt.getTime())) bits.push(dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }));
    const li = loadInTime(hero);
    if (li) {
      // The stored value sometimes already carries its own "Load In" label
      // (e.g. "Load In 3pm"), strip it so we don't print "Load-in Load In 3pm".
      const clean = li.replace(/^\s*load[\s-]?in[:\s]*/i, '').trim();
      bits.push('Load-in ' + (clean || li));
    }
    sub = bits.join(' · ');
  }

  // Tonight's guarantee (the money the TM settles). Same source as booking stats.
  let guarantee = '-';
  if (hero) {
    const fin = (hero.financials || {}) as Record<string, unknown>;
    const g = Number(fin.totalPay ?? fin.guarantee ?? (hero as { guarantee?: number }).guarantee ?? 0);
    if (g > 0) guarantee = '$' + Math.round(g).toLocaleString('en-US');
  }

  // Tonight's hotel (from the hero show's travel rows). Only append a check-in
  // suffix when it's actually a TIME, the field often holds a date, and
  // "· in 2026-07-02" reads wrong, so we drop it in that case.
  let hotel = '-';
  const travel = (hero && Array.isArray(hero.travel) ? hero.travel : []) as ReadonlyArray<Record<string, unknown>>;
  const hotelRow = travel.find((t) => t && (String(t.type || '').toLowerCase() === 'hotel' || !!t.hotel));
  if (hotelRow) {
    const name = String(hotelRow.hotel || hotelRow.vendor || hotelRow.name || 'Lodging');
    const checkIn = String(hotelRow.checkIn || hotelRow.checkInTime || '');
    const isTime = /\d{1,2}:\d{2}|\d\s*(am|pm)/i.test(checkIn);
    hotel = isTime ? name + ' · in ' + checkIn : name;
  }

  // Next venue.
  let next = '-';
  if (nextAfter) {
    const nv = nextAfter.name || nextAfter.venue || 'Untitled';
    const nc = nextAfter.city || '';
    next = nc ? nv + ' · ' + nc : nv;
    const nd = tmDaysUntil(nextAfter.date);
    if (nd > 0 && nd !== Infinity) next += ' · in ' + nd + 'd';
  } else if (hero) {
    next = 'Last show of the run';
  }

  return {
    hasShow: !!hero,
    eyebrow,
    title,
    sub,
    guarantee,
    hotel,
    next,
    next3: confirmedUpcoming.slice(0, 3).map(toTmRow),
  };
}

const EMPTY_TM: TourManagerSpotlightData = {
  hasShow: false,
  eyebrow: '★ Today',
  title: 'No show today',
  sub: "When the band is on the road this card surfaces tonight's guarantee, hotel, and next venue.",
  guarantee: '-',
  hotel: '-',
  next: '-',
  next3: [],
};

export function useTourManagerSpotlight(): { data: TourManagerSpotlightData; loading: boolean } {
  const { data, loading } = useCachedData<TourManagerSpotlightData>('tm-spotlight', async () => {
    /* show-scope 2026-07-29 — member-tier seats see only their own dates.
       User fetched alongside, NOT via getCachedUser: on a cold load the
       cache races this fetch and a null viewer would blank the card. */
    const [res, user] = await Promise.all([
      apiGet<ScheduleResponse>('auth', '/api/data/schedule').catch(() => null),
      fetchCurrentUser().catch(() => null),
    ]);
    return computeTourManagerSpotlight(visibleShows(unwrapEvents(res), user));
  });
  return { data: data ?? EMPTY_TM, loading };
}

// ── Production spotlight ──────────────────────────────────────────────────────
// 1:1 port of production-assets-wiring.js renderNextShowSpotlight: the next show
// header + 3 venue stats (FOH console / Stage / Capacity) and 5 spec tiles
// (Soundcheck / Monitors / Backline / Load-in / FOH eng) + crew notes, sourced
// from the crowd-sourced /api/venues/lookup record (Venue CNTRL).

interface VenueSpecRaw {
  foh_console?: string;
  stage_w?: number; stage_d?: number;
  capacity?: number;
  wedge_count?: number; iem_channels?: number; monitor_console?: string;
  backline_drums?: number | boolean; backline_bass?: number | boolean;
  backline_guitar?: number | boolean; backline_keys?: number | boolean;
  dock_door_size?: string; has_stairs?: number | boolean;
  house_engineer_name?: string; house_ld?: number | boolean;
  soundcheck?: string;
  source?: string;
}
interface VenueLookupRes {
  exists?: boolean;
  spec?: VenueSpecRaw;
  notes?: ReadonlyArray<{ id?: string | number; note?: string; added_by_band_name?: string }>;
}

function fmtStage(s: VenueSpecRaw): string {
  return s.stage_w && s.stage_d ? s.stage_w + "' × " + s.stage_d + "'" : '-';
}
function fmtMonitors(s: VenueSpecRaw): string {
  const b: string[] = [];
  if (s.wedge_count) b.push(s.wedge_count + ' wedges');
  if (s.iem_channels) b.push(s.iem_channels + ' IEM');
  if (s.monitor_console) b.push(String(s.monitor_console));
  return b.length ? b.join(' · ') : '-';
}
function fmtBackline(s: VenueSpecRaw): string {
  const b: string[] = [];
  if (s.backline_drums) b.push('Drums');
  if (s.backline_bass) b.push('Bass');
  if (s.backline_guitar) b.push('Guitar amps');
  if (s.backline_keys) b.push('Keys');
  return b.length ? b.join(' + ') : 'None / BYO';
}
function fmtLoadin(s: VenueSpecRaw): string {
  const b: string[] = [];
  if (s.dock_door_size) b.push('Door ' + s.dock_door_size);
  if (s.has_stairs === 1 || s.has_stairs === true) b.push('Stairs');
  else if (s.has_stairs === 0 || s.has_stairs === false) b.push('No stairs');
  return b.length ? b.join(' · ') : '-';
}

export interface ProdCrewNote {
  readonly id: string;
  readonly note: string;
  readonly band: string;
}

export interface ProductionSpotlightData {
  readonly hasShow: boolean;
  readonly exists: boolean;       // a venue spec record was found
  readonly verified: boolean;
  readonly eyebrow: string;
  readonly title: string;
  readonly sub: string;
  readonly fohConsole: string;
  readonly stage: string;
  readonly capacity: string;
  readonly soundcheck: string;
  readonly monitors: string;
  readonly backline: string;
  readonly loadin: string;
  readonly houseEng: string;
  readonly notes: ReadonlyArray<ProdCrewNote>;
  readonly footerLabel: string;
}

const DASH = '-';
const EMPTY_PROD: ProductionSpotlightData = {
  hasShow: false, exists: false, verified: false,
  eyebrow: '★ Next show', title: 'Nothing on the books yet',
  sub: 'Once a show is confirmed on the schedule it surfaces here with venue specs and crew notes.',
  fohConsole: DASH, stage: DASH, capacity: DASH,
  soundcheck: DASH, monitors: DASH, backline: DASH, loadin: DASH, houseEng: DASH,
  notes: [], footerLabel: 'Venue specs',
};

async function computeProductionSpotlight(events: ReadonlyArray<RawBookingEvent>): Promise<ProductionSpotlightData> {
  const show = findNextShow(events);
  if (!show) return EMPTY_PROD;

  // Header (responsive regardless of the venue lookup result).
  const name = show.name || show.venue || 'Untitled venue';
  const city = show.city || '';
  const out = tmDaysUntil(show.date);
  const when = out === 0 ? 'TODAY' : out === 1 ? 'TOMORROW' : 'IN ' + out + ' DAYS';
  const dt = show.date ? new Date(show.date + 'T00:00:00') : null;
  const header = {
    eyebrow: '★ Next show · ' + when,
    title: city ? name + ', ' + city : name,
    sub: dt && !isNaN(dt.getTime()) ? dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '',
  };

  // Venue spec lookup (crowd-sourced; band-agnostic).
  const q = '/api/venues/lookup?name=' + encodeURIComponent(name) + '&city=' + encodeURIComponent(city);
  const lookup = await apiGet<VenueLookupRes>('auth', q).catch(() => null);

  if (!lookup || !lookup.exists || !lookup.spec) {
    return {
      ...EMPTY_PROD, ...header, hasShow: true, exists: false,
      footerLabel: 'No specs logged yet, be the first to add them.',
    };
  }

  const spec = lookup.spec;
  const verified = spec.source === 'venue_verified';
  // Soundcheck: headliner lineup slot, else dayOfShow.soundcheck.
  const dos = (show.dayOfShow || {}) as { soundcheck?: string };
  const lineup: ReadonlyArray<LineupAct> = Array.isArray(show.lineup) ? (show.lineup as ReadonlyArray<LineupAct>) : [];
  const headliner = lineup.find((a) => a && (a.role === 'headliner' || a.role === 'self')) || lineup[0];
  const soundcheck = (headliner && (headliner as { soundcheckStart?: string }).soundcheckStart) || dos.soundcheck || DASH;

  return {
    hasShow: true,
    exists: true,
    verified,
    ...header,
    fohConsole: spec.foh_console || DASH,
    stage: fmtStage(spec),
    capacity: spec.capacity ? String(spec.capacity) : DASH,
    soundcheck,
    monitors: fmtMonitors(spec),
    backline: fmtBackline(spec),
    loadin: fmtLoadin(spec),
    houseEng: spec.house_engineer_name || (spec.house_ld ? 'House LD on site' : DASH),
    notes: (lookup.notes || []).slice(0, 2).map((n, i) => ({
      id: String(n.id ?? i),
      note: String(n.note || ''),
      band: String(n.added_by_band_name || 'A touring crew'),
    })),
    footerLabel: verified ? 'Verified venue specs' : 'Crew-sourced venue specs',
  };
}

export function useProductionSpotlight(): { data: ProductionSpotlightData; loading: boolean } {
  const { data, loading } = useCachedData<ProductionSpotlightData>('prod-spotlight', async () => {
    /* show-scope 2026-07-29 — member-tier seats see only their own dates.
       User fetched alongside, NOT via getCachedUser: on a cold load the
       cache races this fetch and a null viewer would blank the card. */
    const [res, user] = await Promise.all([
      apiGet<ScheduleResponse>('auth', '/api/data/schedule').catch(() => null),
      fetchCurrentUser().catch(() => null),
    ]);
    return computeProductionSpotlight(visibleShows(unwrapEvents(res), user));
  });
  return { data: data ?? EMPTY_PROD, loading };
}
